import { ShareOutlined } from '@mui/icons-material';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getUsers } from '../../redux/actions/userActions';
import { selectUsers, setSearchQuery, setSearchSelected } from '../../redux/reducers/userReducer';
import { AppDispatch } from '../../redux/store';
import { User } from '../../Types/user';
import DebouncedSearchInput from '../DebouncedSearchInput';
import { useAuth } from '../Providers/AuthProvider';

const ShareDialog = ({
  ownerId,
  currentUsers,
  onShare,
  isOpen,
  setIsOpen,
}: {
  ownerId: User['id'];
  currentUsers: User['id'][];
  onShare: (userIds: User['id'][]) => void;
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
}) => {
  const { currentUser } = useAuth();
  const dispatch = useDispatch<AppDispatch>();
  const { users, searchQuery, searchSelected } = useSelector(selectUsers);

  const [selectedUsers, setSelectedUsers] = useState<User['id'][]>([]);

  const validUsers = useMemo(() => {
    return users?.filter((user) => user.id !== currentUser?.id);
  }, [users, currentUser]);

  const validSelectedUsers = useMemo(
    () => selectedUsers?.filter((userId) => userId !== currentUser?.id),
    [selectedUsers, currentUser]
  );

  useEffect(() => {
    setSelectedUsers(currentUsers);
  }, [isOpen, currentUsers, dispatch]);

  const toggleUser = (userId: User['id']) => {
    if (selectedUsers.includes(userId)) {
      setSelectedUsers((prev) => prev.filter((u) => u !== userId));
    } else {
      setSelectedUsers((prev) => [...prev, userId]);
    }
  };

  const handleShare = useCallback(() => {
    onShare(selectedUsers);
    setIsOpen(false);
  }, [onShare, setIsOpen, selectedUsers]);

  const handleCancel = () => {
    dispatch(setSearchQuery(''));
    setIsOpen(false);
  };

  const disabledCount = useMemo(() => {
    return Math.max(
      users.filter((user) => user.id === currentUser?.id || user.id === ownerId).length,
      0
    );
  }, [users, currentUser, ownerId]);

  const removeCount = useMemo(() => {
    const contributorCount = users.filter((user) => {
      return currentUsers.includes(user.id) && user.id !== currentUser?.id && user.id !== ownerId;
    }).length;
    return Math.max(contributorCount, 0);
  }, [users, currentUser, ownerId, currentUsers]);

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle
          id="alert-dialog-title"
          sx={{ color: '#F900D3', display: 'flex', alignItems: 'center', gap: '10px' }}
        >
          <ShareOutlined /> Share Folder With...
        </DialogTitle>
        <DialogContent>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '150px',
                  padding: '10px 0',
                }}
              >
                <DebouncedSearchInput
                  value={searchQuery}
                  valueUpdated={(value) => {
                    dispatch(setSearchQuery(!isOpen ? '' : value));
                    dispatch(getUsers({ ids: validSelectedUsers }));
                  }}
                  onFocus={() => {
                    dispatch(setSearchSelected(true));
                  }}
                  onBlur={() => {
                    dispatch(setSearchSelected(false));
                  }}
                  shouldFocus={searchSelected}
                />
              </div>
              <div>
                {/* Owner and Current User are disabled and always show */}
                {users
                  .filter((user) => {
                    return user.id === ownerId || user.id === currentUser?.id;
                  })
                  .map((user) => (
                    <div
                      key={user.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '0',
                        paddingBottom: '5px',
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleUser(user.id)}
                    >
                      <Checkbox
                        disabled={true}
                        checked={true}
                        sx={{
                          color: '#757575',
                          '&.Mui-checked': {
                            color: '#757575',
                          },
                          padding: '0',
                        }}
                      />
                      <Typography variant="body2" sx={{ color: '#757575' }}>
                        {`${user.preferredName} - (${user.email})`}
                        {user.id === ownerId ? ' (Owner)' : ''}
                      </Typography>
                    </div>
                  ))}
                {/* Current Contributors up to 10 (including owner and currentUser) */}
                {users
                  .filter((user) => {
                    return (
                      currentUsers.includes(user.id) &&
                      user.id !== currentUser?.id &&
                      user.id !== ownerId
                    );
                  })
                  ?.slice(0, removeCount)
                  .map((user) => (
                    <div
                      key={user.id}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        padding: '0',
                        paddingBottom: '5px',
                        cursor: 'pointer',
                      }}
                      onClick={() => toggleUser(user.id)}
                    >
                      <Checkbox
                        checked={validSelectedUsers.includes(user.id)}
                        sx={{
                          color: '#757575',
                          '&.Mui-checked': {
                            color: '#1DD566',
                          },
                          padding: '0',
                        }}
                      />
                      <Typography variant="body2">{`${user.preferredName} - (${user.email})`}</Typography>
                    </div>
                  ))}
                {/* The remaining spaces can be used by search*/}
                {validUsers?.length === 0 ? (
                  <></>
                ) : (
                  validUsers
                    ?.filter((user) => {
                      return (
                        !currentUsers.includes(user.id) &&
                        user.id !== currentUser?.id &&
                        user.id !== ownerId
                      );
                    })
                    .slice(0, Math.max(10 - (disabledCount + removeCount), 0))
                    .map((user) => (
                      <div
                        key={user.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px',
                          padding: '0',
                          paddingBottom: '5px',
                          cursor: 'pointer',
                        }}
                        onClick={() => toggleUser(user.id)}
                      >
                        <Checkbox
                          checked={validSelectedUsers.includes(user.id)}
                          sx={{
                            color: '#757575',
                            '&.Mui-checked': {
                              color: '#1DD566',
                            },
                            padding: '0',
                          }}
                        />
                        <Typography variant="body2">{`${user.preferredName} - (${user.email})`}</Typography>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={currentUsers === validSelectedUsers}
            onClick={handleShare}
            variant="contained"
            autoFocus
          >
            Share
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ShareDialog;
